import { useState, useContext } from 'react';
import { useParams } from 'react-router';
import { i18n } from 'app/utils/i18n';
import { UserContext } from 'app/state/contexts';
import { useGetDocument } from 'app/api/documents/document-queries';
import { useImagesInDocument } from 'app/api/documents/image-queries';
import { DataImage } from 'app/components';
import Modal from 'react-bootstrap/Modal';

interface BackgroundImagePickerProps {
  value: string;
  onChange: (key: string, value: string) => void;
  variableKey: string;
}

function extractDataUri(value: string): string | null {
  const match = value.match(/url\(['"]?(data:[^'"]+)['"]?\)/);
  return match ? match[1] : null;
}

export function BackgroundImagePicker({ value, onChange, variableKey }: BackgroundImagePickerProps) {
  const [showModal, setShowModal] = useState(false);
  const { documentId } = useParams<{ documentId: string }>();
  const { selectedOrganization } = useContext(UserContext);
  const pDocument = useGetDocument(documentId, selectedOrganization);
  const { data: images, isLoading } = useImagesInDocument(pDocument.data?.superId);

  const hasImage = value && value !== 'none';
  const previewSrc = hasImage ? extractDataUri(value) : null;

  const handleSelect = (image: any) => {
    const dataUri = `data:${image.file.fileType};base64,${image.file.fileContent}`;
    onChange(variableKey, `url('${dataUri}')`);
    setShowModal(false);
  };

  const handleClear = () => {
    onChange(variableKey, 'none');
  };

  return (
    <div className="d-flex align-items-center gap-2">
      {previewSrc && <img src={previewSrc} alt="" className="border rounded" style={{ width: 40, height: 40, objectFit: 'contain' }} />}
      <button className="btn btn-sm btn-outline-primary" onClick={() => setShowModal(true)}>
        <i className="fa-regular fa-image me-1" />
        {hasImage ? i18n('dc.bg.change') : i18n('dc.bg.select')}
      </button>
      {hasImage && (
        <button className="btn btn-sm btn-outline-danger" onClick={handleClear}>
          <i className="fa-regular fa-xmark" />
        </button>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{i18n('dc.bg.modal-title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLoading ? (
            <p className="text-muted">{i18n('dc.bg.loading')}</p>
          ) : !images?.length ? (
            <p className="text-muted">{i18n('dc.bg.no-images')}</p>
          ) : (
            <div className="d-flex flex-wrap gap-2">
              {images.map((image: any) => (
                <div key={image.file.superId} className="border rounded p-1" style={{ width: 120, cursor: 'pointer' }} onClick={() => handleSelect(image)}>
                  <DataImage {...image.file} style={{ width: '100%', height: 80, objectFit: 'contain', display: 'block' }} />
                  <div className="text-truncate fs-7 text-muted mt-1">{image.file?.filename}</div>
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}
